import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { getUserProfile, getPublicTunes, getPublicSets, isFollowing, followUser, unfollowUser, getFollowDoc, updateFollowGenres } from '../utils/socialFirestore';
import PageWrapperAuto from '../components/PageWrapperAuto';
import AppSelect from '../components/AppSelect';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export default function UserPage() {
  const { uid } = useParams();
  const { user } = useAuth();
  const { textStyle } = useUI();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [tunes, setTunes] = useState([]);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followGenres, setFollowGenres] = useState([]); // genres I chose to follow (empty = all)
  const [showGenrePicker, setShowGenrePicker] = useState(false);
  const [pickerGenres, setPickerGenres] = useState([]); // temp selection in picker
  const [genreFilter, setGenreFilter] = useState(''); // filter tunes by genre
  const [typeFilter, setTypeFilter] = useState(''); // filter tunes by type
  const [sortBy, setSortBy] = useState('alpha'); // 'alpha' | 'alphaDesc' | 'recent'
  const [expandedSetId, setExpandedSetId] = useState(null);
  const [importingSetId, setImportingSetId] = useState(null);
  const [importedSetIds, setImportedSetIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('both'); // 'both' | 'tunes' | 'sets'
  const [searchQuery, setSearchQuery] = useState('');

  const isOwnProfile = user?.uid === uid;

  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  async function loadUserData() {
    try {
      const p = await getUserProfile(uid);
      setProfile(p);

      if (p && p.visibility === 'public') {
        const promises = [getPublicTunes(uid, p), getPublicSets(uid, p)];
        if (user && user.uid !== uid) {
          promises.push(getFollowDoc(user.uid, uid));
        }
        const results = await Promise.all(promises);
        setTunes(results[0]);
        setSets(results[1]);
        if (results[2] !== undefined) {
          setFollowing(!!results[2]);
          setFollowGenres(results[2]?.genres || []);
        }
      }
    } catch (err) {
      setError('Could not load this profile.');
    }
    setLoading(false);
  }

  async function handleFollowToggle() {
    if (!user || isOwnProfile) return;
    setFollowLoading(true);
    try {
      if (following) {
        await unfollowUser(user.uid, uid);
        setFollowing(false);
        setFollowGenres([]);
      } else {
        // If target has genres, show picker; otherwise follow all
        const targetGenres = profile?.genres || [];
        if (targetGenres.length > 0) {
          setPickerGenres([...targetGenres]); // default: all selected
          setShowGenrePicker(true);
        } else {
          await followUser(user.uid, uid, []);
          setFollowing(true);
          setFollowGenres([]);
        }
      }
    } catch {
      // silently fail
    }
    setFollowLoading(false);
  }

  async function handleGenrePickerSave() {
    setFollowLoading(true);
    try {
      // If all genres selected, store empty array (= follow all)
      const targetGenres = profile?.genres || [];
      const genresToSave = pickerGenres.length === targetGenres.length ? [] : pickerGenres;
      if (following) {
        // Editing existing follow
        await updateFollowGenres(user.uid, uid, genresToSave);
      } else {
        // New follow
        await followUser(user.uid, uid, genresToSave);
        setFollowing(true);
      }
      setFollowGenres(genresToSave);
      setShowGenrePicker(false);
    } catch {
      // silently fail
    }
    setFollowLoading(false);
  }

  async function handleImportSet(set) {
    if (!user || importingSetId) return;
    setImportingSetId(set.id);
    try {
      const sourceName = profile?.displayName || profile?.username || 'another musician';
      const sourceUsername = profile?.username ? `@${profile.username}` : '';
      const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;

      // Import each tune in the set
      const newTuneIds = [];
      for (const tuneRef of (set.tunes || [])) {
        const tuneId = tuneRef.id || tuneRef;
        const tuneSnap = await getDoc(doc(db, 'users', uid, 'tunes', tuneId));
        if (!tuneSnap.exists()) continue;
        const td = tuneSnap.data();
        const newTuneId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
          id: newTuneId,
          title: td.title || '', type: td.type || '', key: td.key || '',
          genre: td.genre || '', composer: td.composer || '',
          abc: td.abc || '', lyrics: td.lyrics || '', chords: td.chords || '',
          notes: '', link1: td.link1 || '', link2: td.link2 || '', link3: td.link3 || '',
          customTags: [], status: '', rating: '',
          source: `Imported from ${sourceLabel}`,
          importedFrom: { uid, tuneId, displayName: sourceName, username: profile?.username || '' },
          sessions: [], sets: [], recordings: [], attachments: [],
          createdAt: serverTimestamp()
        });
        newTuneIds.push({ id: newTuneId, title: td.title || '' });
      }

      // Create the set
      const newSetId = crypto.randomUUID();
      await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
        id: newSetId,
        title: set.title || 'Untitled Set',
        tunes: newTuneIds.map(t => ({ id: t.id, title: t.title })),
        notes: set.notes || '',
        source: `Imported from ${sourceLabel}`,
        importedFrom: { uid, setId: set.id, displayName: sourceName, username: profile?.username || '' },
        customTags: [], rating: '',
        createdAt: serverTimestamp()
      });

      setImportedSetIds(prev => new Set([...prev, set.id]));
      alert(`Imported "${set.title}" with ${newTuneIds.length} tune(s).`);
    } catch (err) {
      console.error('Failed to import set:', err);
      alert('Failed to import set. Please try again.');
    }
    setImportingSetId(null);
  }

  // Derive available genres and types from tunes
  const tuneGenres = [...new Set(tunes.map(t => t.genre).filter(Boolean))].sort();
  const tuneTypes = [...new Set(tunes.map(t => t.type).filter(Boolean))].sort();
  const typeOptions = [{ value: '', label: 'All types' }, ...tuneTypes.map((t) => ({ value: t, label: t }))];
  const sortOptions = [
    { value: 'alpha', label: 'A-Z' },
    { value: 'alphaDesc', label: 'Z-A' },
    { value: 'recent', label: 'Newest' },
  ];

  // Filter and sort tunes
  const displayedTunes = tunes
    .filter(t => !searchQuery || (t.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(t => !genreFilter || t.genre === genreFilter)
    .filter(t => !typeFilter || t.type === typeFilter)
    .sort((a, b) => {
      if (sortBy === 'recent') return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
      if (sortBy === 'alphaDesc') return (b.title || '').localeCompare(a.title || '');
      return (a.title || '').localeCompare(b.title || '');
    });

  // Filter sets by search query
  const displayedSets = sets
    .filter(s => !searchQuery || (s.title || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const chipStyle = (selected) => ({
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    border: selected
      ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 64%, var(--card-border) 36%)'
      : '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
    backgroundColor: selected
      ? 'color-mix(in srgb, var(--text-link, #228be6) 24%, var(--input-bg) 76%)'
      : 'color-mix(in srgb, var(--input-bg) 90%, transparent)',
    color: 'var(--text-primary)',
    display: 'inline-block'
  });

  if (loading) return <PageWrapperAuto><p style={{ textAlign: 'center' }}>Loading...</p></PageWrapperAuto>;

  if (error) return (
    <PageWrapperAuto>
      <div style={{ padding: '2rem' }}>
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
          <button className="btn-subtle search-control-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
        <div style={{ textAlign: 'center' }}>
        <p style={textStyle}>{error}</p>
        </div>
      </div>
    </PageWrapperAuto>
  );

  if (!profile || profile.visibility !== 'public') {
    return (
      <PageWrapperAuto>
        <div style={{ padding: '2rem' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
            <button className="btn-subtle search-control-btn" onClick={() => navigate(-1)}>← Back</button>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h3 style={textStyle}>Profile Not Available</h3>
            <p style={{ ...textStyle, opacity: 0.7, marginTop: '0.5rem' }}>
              This profile is private or does not exist.
            </p>
          </div>
        </div>
      </PageWrapperAuto>
    );
  }

  return (
    <PageWrapperAuto>
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '1rem' }}>
        <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'flex-start' }}>
          <button className="btn-subtle search-control-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
        <div className="app-title" style={{ marginBottom: '1rem' }}>
          Profile
        </div>

        {/* Profile Header */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          {profile.photoURL && (
            <img
              src={profile.photoURL}
              alt=""
              style={{ width: '64px', height: '64px', borderRadius: '50%', marginBottom: '8px' }}
            />
          )}
          <h2 style={textStyle}>{profile.displayName || 'Musician'}</h2>

          {profile.location && (
            <p style={{ ...textStyle, opacity: 0.7, margin: '2px 0' }}>{profile.location}</p>
          )}

          {profile.bio && (
            <p style={{ ...textStyle, marginTop: '0.5rem' }}>{profile.bio}</p>
          )}

          {profile.instruments?.length > 0 && (
            <p style={{ ...textStyle, margin: '4px 0' }}>
              <strong>Instruments:</strong> {profile.instruments.join(', ')}
            </p>
          )}

          {profile.genres?.length > 0 && (
            <p style={{ ...textStyle, margin: '4px 0' }}>
              <strong>Categories:</strong> {profile.genres.join(', ')}
            </p>
          )}
        </div>

        {isOwnProfile ? (
          <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            <button className="btn-subtle search-control-btn" onClick={() => navigate('/social/profile')}>
              Edit Profile
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '0.75rem', display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <button
              className="btn-subtle search-control-btn"
              onClick={handleFollowToggle}
              disabled={followLoading}
              style={{ padding: '8px 24px' }}
            >
              {followLoading ? '...' : following ? 'Unfollow' : 'Follow'}
            </button>
            {following && (profile?.genres || []).length > 0 && (
              <button
                className="btn-subtle search-control-btn"
                onClick={() => {
                  const targetGenres = profile?.genres || [];
                  setPickerGenres(followGenres.length > 0 ? [...followGenres] : [...targetGenres]);
                  setShowGenrePicker(true);
                }}
                style={{ padding: '8px 16px' }}
              >
                Categories{followGenres.length > 0 ? ` (${followGenres.length})` : ''}
              </button>
            )}
          </div>
        )}

        {/* View toggle + search */}
        {(tunes.length > 0 || sets.length > 0) && (
          <div style={{ marginTop: '1.25rem' }}>
            {/* View mode tabs */}
            {tunes.length > 0 && sets.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                <div className="mode-switcher-pill">
                {['both', 'tunes', 'sets'].map(mode => (
                  <button
                    key={mode}
                    className={`mode-switcher-btn ${viewMode === mode ? 'mode-switcher-btn-active' : ''}`}
                    onClick={() => setViewMode(mode)}
                  >
                    {mode === 'both' ? 'All' : mode === 'tunes' ? `Items (${tunes.length})` : `Sets (${sets.length})`}
                  </button>
                ))}
                </div>
              </div>
            )}
            {/* Search bar */}
            <input
              className="search-input"
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '6px 10px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                backgroundColor: 'var(--input-bg)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        )}

        {/* Public Tunes */}
        {(viewMode === 'both' || viewMode === 'tunes') && tunes.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={textStyle}>Items ({tunes.length})</h4>

            {/* Filter/sort controls */}
            {tunes.length > 3 && (
              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                {/* Category chips */}
                {tuneGenres.length > 1 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                    <span
                      onClick={() => setGenreFilter('')}
                      style={chipStyle(!genreFilter)}
                    >
                      All
                    </span>
                    {tuneGenres.map(g => (
                      <span
                        key={g}
                        onClick={() => setGenreFilter(genreFilter === g ? '' : g)}
                        style={chipStyle(genreFilter === g)}
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                )}
                {/* Type + sort row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '6px',
                    alignItems: 'center'
                  }}
                >
                  {tuneTypes.length > 1 && (
                    <AppSelect
                      className="compact-action-select"
                      value={typeFilter}
                      onChange={setTypeFilter}
                      options={typeOptions}
                      active={Boolean(typeFilter)}
                      style={{ fontSize: '0.8rem' }}
                    />
                  )}
                  <AppSelect
                    className="compact-action-select search-sort-select"
                    value={sortBy}
                    onChange={setSortBy}
                    options={sortOptions}
                    active={Boolean(sortBy && sortBy !== 'alpha')}
                    style={{
                      fontSize: '0.8rem',
                      gridColumn: tuneTypes.length > 1 ? 'auto' : '1 / -1'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Followed categories notice */}
            {following && followGenres.length > 0 && (
              <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginBottom: '6px' }}>
                Showing categories you follow: {followGenres.join(', ')}
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '0.5rem' }}>
              {displayedTunes.map(tune => (
                <div
                  key={tune.id}
                  onClick={() => navigate(`/user/${uid}/tune/${tune.id}`)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  <strong style={textStyle}>{tune.title}</strong>
                  <span style={{ ...textStyle, opacity: 0.7, marginLeft: '8px', fontSize: '0.85em' }}>
                    {[tune.type, tune.key, tune.genre].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ))}
              {displayedTunes.length === 0 && (
                <p style={{ ...textStyle, opacity: 0.6, textAlign: 'center', fontSize: '0.85rem' }}>
                  No items match the current filters.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Public Sets */}
        {(viewMode === 'both' || viewMode === 'sets') && sets.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={textStyle}>Sets ({displayedSets.length})</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '0.5rem' }}>
              {displayedSets.map(set => {
                const isExpanded = expandedSetId === set.id;
                const isImported = importedSetIds.has(set.id);
                const isImporting = importingSetId === set.id;
                return (
                <div key={set.id}>
                  <div
                    onClick={() => setExpandedSetId(isExpanded ? null : set.id)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong style={textStyle}>{set.title}</strong>
                      {Array.isArray(set.tunes) && (
                        <span style={{ ...textStyle, opacity: 0.7, marginLeft: '8px', fontSize: '0.85em' }}>
                          {set.tunes.length} tune(s)
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.8rem', opacity: 0.5, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  </div>
                  {isExpanded && (
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: '0 0 8px 8px',
                      borderTop: '1px solid var(--card-border, #ddd)',
                    }}>
                      {Array.isArray(set.tunes) && set.tunes.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                          {set.tunes.map((t, i) => (
                            <li key={t.id || i} style={{ padding: '3px 0', fontSize: '0.9rem' }}>
                              <span
                                style={{ ...textStyle, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--card-border)' }}
                                onClick={() => navigate(`/user/${uid}/tune/${t.id}`)}
                              >
                                {t.title || 'Untitled'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ ...textStyle, opacity: 0.6, fontSize: '0.85rem', margin: '4px 0' }}>No tunes in this set.</p>
                      )}
                      {!isOwnProfile && user && (
                        <button
                          className="btn-subtle search-control-btn"
                          onClick={() => handleImportSet(set)}
                          disabled={isImporting || isImported}
                          style={{ fontSize: '0.85rem', padding: '6px 14px' }}
                        >
                          {isImported ? '✓ Imported' : isImporting ? 'Importing...' : '📥 Import Set'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
              {displayedSets.length === 0 && (
                <p style={{ ...textStyle, opacity: 0.6, textAlign: 'center', fontSize: '0.85rem' }}>
                  No sets match the current search.
                </p>
              )}
            </div>
          </div>
        )}

        {tunes.length === 0 && sets.length === 0 && (
          <p style={{ ...textStyle, textAlign: 'center', marginTop: '1.5rem', opacity: 0.7 }}>
            This musician hasn't shared any items or sets yet.
          </p>
        )}
      </div>

      {/* Genre picker overlay */}
      {showGenrePicker && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1rem'
        }}
          onClick={() => setShowGenrePicker(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary, #fff)',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '360px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ ...textStyle, marginTop: 0, textAlign: 'center' }}>
              {following ? 'Followed Categories' : 'Which categories to follow?'}
            </h3>
            <p style={{ ...textStyle, fontSize: '0.85rem', opacity: 0.7, textAlign: 'center', marginBottom: '1rem' }}>
              Select the categories you want to see from {profile?.displayName || 'this musician'}:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
              {(profile?.genres || []).map(genre => (
                <label key={genre} style={{
                  ...textStyle,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  cursor: 'pointer', padding: '6px 8px',
                  backgroundColor: 'var(--input-bg)', borderRadius: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={pickerGenres.includes(genre)}
                    onChange={() => {
                      setPickerGenres(prev =>
                        prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
                      );
                    }}
                  />
                  {genre}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                className="btn-subtle search-control-btn"
                onClick={handleGenrePickerSave}
                disabled={followLoading || pickerGenres.length === 0}
                style={{ padding: '8px 20px' }}
              >
                {followLoading ? '...' : following ? 'Save' : 'Follow'}
              </button>
              <button
                className="btn-subtle search-control-btn"
                onClick={() => setShowGenrePicker(false)}
                style={{ padding: '8px 20px' }}
              >
                Cancel
              </button>
            </div>
            {pickerGenres.length === 0 && (
              <p style={{ ...textStyle, fontSize: '0.8rem', color: '#e44', textAlign: 'center', marginTop: '6px' }}>
                Select at least one category.
              </p>
            )}
          </div>
        </div>
      )}
    </PageWrapperAuto>
  );
}
